import { useEffect, useState } from "react";
import { fechaCorta } from "../../lib/fecha-ui";
import { olvidarPedidoCatalogo, verPedidoCatalogo } from "../../lib/abrir-catalogo";
import { solesStrADm } from "@huayruro/shared";
import { useApi, mutar } from "../../lib/useApi";
import { solesDm } from "../../lib/money";
import { SelectorProducto, type ProductoRef } from "../../components/SelectorProducto";
import { Card, Button, Input, useToast } from "../../components/ui";
import type { SesionActiva } from "../../lib/tipos";

// Fila del catálogo maestro nacional (B7): referencia SUSALUD/DIGEMID para el alta asistida.
type MaestroFila = {
  id: string;
  gtin: string | null;
  nombre: string;
  dci: string | null;
  concentracion: string | null;
  forma: string | null;
  laboratorio: string | null;
  presentacion: string | null;
  unidades_envase: number | null;
};

// Δ4: producto marcado como tratamiento crónico (insumo de la bandeja de reposición A2).
type ProductoCronico = { id: string; nombre: string; presentacion: string | null; principio_activo: string | null; dosis_diaria_default: number | null };

type Presentacion = { id: string; producto_id: string; nombre: string; factor_unidades: number; es_base: number };
type Precio = { presentacion_id: string; precio_sin_igv_dm: number; precio_total_dm: number };
type Sucursal = { id: string; nombre: string };

// «Sí lo tengo» del Mostrador (RU-1, tramo 07): producto en la mano sin ficha, anotado para el Encargado.
type NotaPorCrear = { id: string; sucursal: string; gtin: string | null; descripcion: string | null; fecha_hora: string; anotado_por: string | null };

// Lo que llega ya puesto al alta: desde el Mostrador («Crear producto») o desde una nota «Por crear».
// `n` cambia con cada pedido para que el formulario se vuelva a armar con los datos nuevos.
type AltaInicial = { gtin: string | null; nombre: string | null; notaId: string | null; n: number };

// Select nativo con el estilo del tema claro (no hay primitivo <Select> en el barrel).
const SELECT_CLS =
  "w-full box-border rounded-[9px] border border-line-input bg-field px-3 py-2.5 text-[13px] text-ink outline-none";

const aDm = (s: string): number | null => {
  const t = s.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,4})?$/.test(t)) return null;
  try {
    return solesStrADm(t);
  } catch {
    return null;
  }
};

// Formularios de catálogo (§11.1): crear producto (+ presentación base), agregar presentaciones
// (Δ1: blíster/caja con factor) y fijar precios por sucursal.
export function CatalogoForm({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  // Lo que otra pantalla pidió abrir (tramo 07): se lee al armar el estado y se olvida después de montar.
  const [pedido] = useState(verPedidoCatalogo);
  useEffect(() => olvidarPedidoCatalogo(), []);
  const [producto, setProducto] = useState<ProductoRef | null>(pedido?.tipo === "editar" ? { id: pedido.productoId, nombre: pedido.nombre } : null);
  const [alta, setAlta] = useState<AltaInicial | null>(pedido?.tipo === "alta" ? { gtin: pedido.gtin, nombre: pedido.nombre, notaId: null, n: 1 } : null);
  const [recargaNotas, setRecargaNotas] = useState(0);
  const sucursales = useApi<{ sucursales: Sucursal[] }>(esSuper ? "/sucursales" : null);
  const [sucursalId, setSucursalId] = useState(pedido?.sucursalId ?? "");
  const sucursalEfectiva = esSuper ? sucursalId : sesion.usuario.sucursalId ?? "";

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
      <p className="text-[13px] text-ink-2">
        Da de alta un producto (búscalo en el catálogo nacional y ajusta solo el precio) o edita precios y
        presentaciones por botica.
      </p>
      <ConteosCatalogo />

      <PorCrear
        esSuper={esSuper}
        recarga={recargaNotas}
        onCrear={(nota) => setAlta({ gtin: nota.gtin, nombre: nota.descripcion, notaId: nota.id, n: (alta?.n ?? 0) + 1 })}
      />

      <CrearProducto
        key={alta?.n ?? 0}
        inicial={alta}
        onCreado={(p) => {
          setProducto(p);
          setAlta(null);
          setRecargaNotas((n) => n + 1);
        }}
      />

      <Card className="gap-3">
        <h2 className="text-[13.5px] font-bold text-ink">Editar precios y presentaciones</h2>
        {esSuper && (
          <select value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} className={SELECT_CLS}>
            <option value="">— Elige la botica para los precios —</option>
            {(sucursales.data?.sucursales ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        )}
        {producto ? (
          <>
            <div className="flex items-center justify-between gap-2 rounded-[9px] border border-line bg-inset px-3 py-2.5">
              <span className="truncate text-[13px] font-semibold text-ink">{producto.nombre}</span>
              <button onClick={() => setProducto(null)} className="shrink-0 px-1 text-[12px] font-medium text-link hover:text-link-hover max-md:min-h-[44px]">
                cambiar
              </button>
            </div>
            <AccionesProducto
              key={producto.id}
              producto={producto}
              esSuper={esSuper}
              onRenombrado={(nombre) => setProducto({ ...producto, nombre })}
              onDadoDeBaja={() => setProducto(null)}
            />
          </>
        ) : (
          <SelectorProducto onSelect={setProducto} placeholder="Buscar producto para editar..." />
        )}

        {producto && (esSuper ? sucursalEfectiva : true) ? (
          <EditorPrecios productoId={producto.id} esSuper={esSuper} sucursalId={sucursalEfectiva} />
        ) : producto && esSuper && !sucursalEfectiva ? (
          <p className="text-[13px] text-warn">Elige una botica para ver o editar precios.</p>
        ) : null}
      </Card>

      <Cronicos />
    </div>
  );
}

// B4 (tramo 07): cuántos productos tiene el catálogo, separando los de prueba (decisión de Kevin del tramo
// 06: «N reales · N de prueba»), y cuántos medicamentos trae el catálogo nacional. Se veían en Ajustes
// hasta que el tramo 03 los sacó sin querer (commit 2270ca0).
function ConteosCatalogo() {
  const cat = useApi<{ activos: number; reales: number; prueba: number }>("/catalogo/conteo");
  const nac = useApi<{ total: number }>("/maestro/conteo");
  const n = (x: number) => x.toLocaleString("es-PE");
  if (!cat.data && !nac.data) return null;
  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-ink-2" data-testid="conteos-catalogo">
      {cat.data && (
        <span>
          Tu catálogo: <strong className="tabular-nums text-ink">{n(cat.data.reales)}</strong> {cat.data.reales === 1 ? "real" : "reales"} ·{" "}
          <span className="tabular-nums">{n(cat.data.prueba)}</span> de prueba
        </span>
      )}
      {nac.data && (
        <span>
          Catálogo nacional: <span className="tabular-nums">{n(nac.data.total)}</span> medicamentos
        </span>
      )}
    </p>
  );
}

// B2 (C-3) «Editar datos» — Encargado y Dueño — y B3 (C-4) «Dar de baja» — solo el Dueño (K-1 de Kevin).
// Dar de baja es un borrado blando que la app no sabe deshacer: la confirmación lo dice con esas palabras.
type Ficha = { id: string; nombre: string; presentacion: string | null; laboratorio: string | null; principio_activo: string | null; categoria: string | null; tipo?: string | null; requiere_receta: number };
function AccionesProducto({
  producto,
  esSuper,
  onRenombrado,
  onDadoDeBaja,
}: {
  producto: ProductoRef;
  esSuper: boolean;
  onRenombrado: (nombre: string) => void;
  onDadoDeBaja: () => void;
}) {
  const toast = useToast();
  const [modo, setModo] = useState<"nada" | "datos" | "baja">("nada");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function darDeBaja() {
    setOcupado(true);
    setError(null);
    try {
      await mutar(`/catalogo/productos/${producto.id}`, { method: "DELETE" });
      toast(`«${producto.nombre}» dado de baja.`);
      onDadoDeBaja();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setOcupado(false);
    }
  }

  return (
    <div className="space-y-2">
      {modo === "nada" && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setModo("datos")} className="max-md:min-h-[44px]">
            Editar datos
          </Button>
          {esSuper && (
            <Button variant="outline" size="sm" onClick={() => setModo("baja")} className="max-md:min-h-[44px]">
              Dar de baja
            </Button>
          )}
        </div>
      )}
      {modo === "datos" && (
        <DatosProducto
          productoId={producto.id}
          onCancelar={() => setModo("nada")}
          onGuardado={(nombre) => {
            onRenombrado(nombre);
            setModo("nada");
          }}
        />
      )}
      {modo === "baja" && (
        <div role="alertdialog" aria-label="Dar de baja" className="space-y-2 rounded-[9px] border border-accent-soft-2 bg-accent-soft px-3 py-3">
          <p className="text-[13px] font-semibold text-ink">¿Dar de baja «{producto.nombre}»?</p>
          <p className="text-[12.5px] text-ink-2">
            Deja de aparecer en el mostrador y en el catálogo de todas las boticas. La app no tiene cómo deshacerlo.
          </p>
          {error && <p className="text-[12px] text-accent-ink">{error}</p>}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setModo("nada")} disabled={ocupado} className="max-md:min-h-[44px]">
              No, volver
            </Button>
            <Button size="sm" onClick={() => void darDeBaja()} disabled={ocupado} className="max-md:min-h-[44px]">
              {ocupado ? "Dando de baja…" : "Sí, dar de baja"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DatosProducto({ productoId, onCancelar, onGuardado }: { productoId: string; onCancelar: () => void; onGuardado: (nombre: string) => void }) {
  const toast = useToast();
  const ficha = useApi<{ producto: Ficha }>(`/catalogo/productos/${productoId}`, [productoId]);
  const [f, setF] = useState<Ficha | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (ficha.data) setF(ficha.data.producto);
  }, [ficha.data]);

  if (ficha.error) return <p className="text-[12.5px] text-accent-ink">{ficha.error}</p>;
  if (!f) return <p className="py-2 text-[13px] text-ink-3">Cargando la ficha…</p>;
  const campo = (k: "presentacion" | "laboratorio" | "principio_activo" | "categoria", etiqueta: string) => (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-semibold text-ink-2">{etiqueta}</span>
      <Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );

  async function guardar() {
    if (!f || !f.nombre.trim()) {
      setError("El nombre no puede quedar vacío.");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await mutar(`/catalogo/productos/${productoId}`, {
        method: "PATCH",
        body: {
          nombre: f.nombre,
          presentacion: f.presentacion ?? "",
          laboratorio: f.laboratorio ?? "",
          principio_activo: f.principio_activo ?? "",
          categoria: f.categoria ?? "",
          requiere_receta: f.requiere_receta === 1,
          tipo: f.tipo ?? "",
        },
      });
      toast("Datos del producto guardados.");
      onGuardado(f.nombre.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-2 rounded-[9px] border border-line-inset bg-inset p-3">
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-2">Nombre</span>
        <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
      </label>
      <div className="grid gap-2 sm:grid-cols-2">
        {campo("presentacion", "Presentación")}
        {campo("laboratorio", "Laboratorio")}
        {campo("principio_activo", "Principio activo")}
        {campo("categoria", "Categoría")}
      </div>
      {/* Buscador del Mostrador (Kevin, 16-oct-2026): el color de fondo y del nombre. Automático = marca o
          genérico según el nombre; aseo y galénico solo se saben si la botica los marca. */}
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-2">Tipo en el buscador</span>
        <select
          value={f.tipo ?? ""}
          onChange={(e) => setF({ ...f, tipo: e.target.value || null })}
          className="min-h-[40px] rounded-[9px] border border-line-input bg-field px-2 text-[13.5px] text-ink max-md:min-h-[44px]"
        >
          <option value="">Automático (marca o genérico, por el nombre)</option>
          <option value="marca">Marca</option>
          <option value="generico">Genérico</option>
          <option value="aseo">Aseo</option>
          <option value="galenico">Galénico</option>
        </select>
      </label>
      <label className="flex items-center gap-2 text-[13px] text-ink-2 max-md:min-h-[44px]">
        <input type="checkbox" className="accent-accent" checked={f.requiere_receta === 1} onChange={(e) => setF({ ...f, requiere_receta: e.target.checked ? 1 : 0 })} />
        Requiere receta
      </label>
      {error && <p className="text-[12px] text-accent-ink">{error}</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancelar} disabled={guardando} className="max-md:min-h-[44px]">
          Cancelar
        </Button>
        <Button size="sm" onClick={() => void guardar()} disabled={guardando} className="max-md:min-h-[44px]">
          {guardando ? "Guardando…" : "Guardar datos"}
        </Button>
      </div>
    </div>
  );
}

// «Por crear» (RU-1 y decisión (c) de Kevin del tramo 07): lo que en el mostrador se anotó con «Sí lo
// tengo» — el producto está en la botica y no tiene ficha. Vive en Catálogo porque aquí se crea la
// ficha. El Encargado ve las de su botica; el Dueño las de todas. Sin notas, el bloque no se muestra.
function PorCrear({ esSuper, recarga, onCrear }: { esSuper: boolean; recarga: number; onCrear: (n: NotaPorCrear) => void }) {
  const toast = useToast();
  const lista = useApi<{ notas: NotaPorCrear[] }>("/productos-por-crear", [recarga]);
  const [confirmar, setConfirmar] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const notas = lista.data?.notas ?? [];

  async function descartar(n: NotaPorCrear) {
    setOcupado(true);
    try {
      await mutar(`/productos-por-crear/${n.id}`, { method: "PATCH", body: { resolucion: "descartado" } });
      toast("Nota descartada.");
      setConfirmar(null);
      lista.recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  if (notas.length === 0) return null;

  return (
    <Card className="gap-3">
      <h2 className="text-[13.5px] font-bold text-ink">Por crear ({notas.length})</h2>
      <p className="text-[12.5px] leading-relaxed text-ink-2">
        Lo anotaron en el mostrador con «Sí lo tengo»: el producto está en la botica y todavía no tiene ficha.
      </p>
      <ul className="flex flex-col divide-y divide-line-inset">
        {notas.map((n) => (
          <li key={n.id} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-ink">{n.descripcion ?? "Sin nombre"}</p>
              <p className="truncate text-[12px] text-ink-2">
                {[n.gtin ? `Código ${n.gtin}` : null, n.anotado_por, fechaCorta(n.fecha_hora), esSuper ? n.sucursal : null].filter(Boolean).join(" · ")}
              </p>
            </div>
            {confirmar === n.id ? (
              <div className="flex flex-none items-center gap-2">
                <span className="text-[12.5px] text-ink-2">¿Descartar?</span>
                <Button size="sm" variant="outline" onClick={() => setConfirmar(null)} disabled={ocupado} className="max-md:min-h-[44px]">
                  No
                </Button>
                <Button size="sm" onClick={() => void descartar(n)} disabled={ocupado} className="max-md:min-h-[44px]">
                  Sí, descartar
                </Button>
              </div>
            ) : (
              <div className="flex flex-none items-center gap-2">
                <Button size="sm" onClick={() => onCrear(n)} className="max-md:min-h-[44px]">
                  Crear producto
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmar(n.id)} className="max-md:min-h-[44px]">
                  Descartar
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// Δ4 — Tratamientos crónicos. Vive en Catálogo porque es un atributo del producto (qué se toma todos
// los días y cuánto), no de una botica: se marca una vez para toda la cadena.
//
// Es el insumo ÚNICO de la bandeja de reposición (A2): sin productos marcados acá, esa pantalla no
// puede calcular la fecha de agotamiento de nadie y nace vacía. Por eso la lista de marcados se ve
// entera de un vistazo — es una lista corta que se cura a mano (20–40 SKU) y hay que poder auditarla.
function Cronicos() {
  const toast = useToast();
  const lista = useApi<{ productos: ProductoCronico[] }>("/catalogo/cronicos");
  const [nuevo, setNuevo] = useState<ProductoRef | null>(null);
  const [dosis, setDosis] = useState("1");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productos = lista.data?.productos ?? [];

  async function marcar(id: string, esCronico: boolean, dosisDiaria: string | null, nombre: string) {
    setGuardando(true);
    setError(null);
    try {
      await mutar(`/catalogo/productos/${id}/cronico`, {
        method: "PUT",
        body: { es_cronico: esCronico, dosis_diaria: dosisDiaria === null ? null : Number(dosisDiaria.replace(",", ".")) },
      });
      toast(esCronico ? `${nombre}: marcado como crónico.` : `${nombre}: ya no es crónico.`);
      setNuevo(null);
      setDosis("1");
      lista.recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Card className="gap-3">
      <h2 className="text-[13.5px] font-bold text-ink">Tratamientos crónicos</h2>
      <p className="text-[12.5px] leading-relaxed text-ink-2">
        Los que la persona toma todos los días (presión, diabetes, anticonceptivos). Con la dosis diaria, el sistema sabe
        cuándo se le acaba y arma la <strong>bandeja de reposición</strong>. Sin esto, esa bandeja está vacía.
      </p>

      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-semibold text-ink-2">Producto</span>
          {nuevo ? (
            <div className="flex items-center justify-between gap-2 rounded-[9px] border border-line-input bg-field px-3 py-2">
              <span className="truncate text-[13px] text-ink">{nuevo.nombre}</span>
              <button onClick={() => setNuevo(null)} className="shrink-0 text-[12px] text-link underline">cambiar</button>
            </div>
          ) : (
            <SelectorProducto onSelect={setNuevo} placeholder="Buscar el producto a marcar…" />
          )}
        </label>
        {/* El ancho lo pone el contenedor: el primitivo Input trae `w-full` y `cn` no hace merge de
            utilidades, así que una clase de ancho en el propio input no gana. */}
        <label className="flex flex-col gap-1.5 sm:w-[110px]">
          <span className="text-[12px] font-semibold text-ink-2">Cuánto al día</span>
          <Input value={dosis} onChange={(e) => setDosis(e.target.value)} inputMode="decimal" placeholder="1" />
        </label>
        <Button onClick={() => void marcar(nuevo?.id ?? "", true, dosis, nuevo?.nombre ?? "")} disabled={!nuevo || !dosis.trim() || guardando} className="max-md:min-h-[44px] justify-center">
          {guardando ? "Guardando…" : "Marcar"}
        </Button>
      </div>
      <p className="text-[11.5px] text-ink-3">
        En unidades por día: 1 tableta diaria = <strong>1</strong>; dos veces al día = <strong>2</strong>; media al día ={" "}
        <strong>0.5</strong>.
      </p>

      {error && <p className="text-[12px] text-accent-ink">{error}</p>}

      {lista.cargando ? (
        <p className="py-2 text-[13px] text-ink-3">Cargando…</p>
      ) : productos.length === 0 ? (
        <p className="rounded-[9px] border border-dashed border-line-empty px-3 py-3 text-center text-[12.5px] text-ink-3">
          Todavía no marcaste ninguno.
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-line-inset">
          {productos.map((p) => (
            <FilaCronico key={p.id} p={p} onGuardar={marcar} ocupado={guardando} />
          ))}
        </div>
      )}
    </Card>
  );
}

function FilaCronico({
  p,
  onGuardar,
  ocupado,
}: {
  p: ProductoCronico;
  onGuardar: (id: string, esCronico: boolean, dosis: string | null, nombre: string) => Promise<void>;
  ocupado: boolean;
}) {
  const original = String(p.dosis_diaria_default ?? "");
  const [dosis, setDosis] = useState(original);
  const sucio = dosis.trim() !== original;

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-ink">{p.nombre}</p>
        <p className="truncate text-[11.5px] text-ink-3">{p.principio_activo ?? p.presentacion ?? "—"}</p>
      </div>
      <div className="flex flex-none items-center gap-1.5">
        <div className="w-[74px]">
          <Input value={dosis} onChange={(e) => setDosis(e.target.value)} inputMode="decimal" className="text-center" aria-label={`Dosis diaria de ${p.nombre}`} />
        </div>
        <span className="whitespace-nowrap text-[12px] text-ink-3">al día</span>
        {sucio && (
          <Button size="sm" onClick={() => void onGuardar(p.id, true, dosis, p.nombre)} disabled={ocupado || !dosis.trim()} className="max-md:min-h-[44px]">
            Guardar
          </Button>
        )}
        <button
          onClick={() => void onGuardar(p.id, false, null, p.nombre)}
          disabled={ocupado}
          className="rounded-[8px] px-2 py-1 text-[12px] text-ink-3 hover:bg-hover-btn hover:text-accent-ink disabled:opacity-50 max-md:min-h-[44px]"
          aria-label={`Quitar ${p.nombre} de crónicos`}
        >
          Quitar
        </button>
      </div>
    </div>
  );
}

const FORM_VACIO = { nombre: "", presentacion: "", laboratorio: "", principio_activo: "", categoria: "", requiere_receta: false, codigo_barras: "" };

function CrearProducto({ inicial, onCreado }: { inicial: AltaInicial | null; onCreado: (p: ProductoRef) => void }) {
  const toast = useToast();
  const [abierto, setAbierto] = useState(inicial !== null);
  const [f, setF] = useState(() => ({ ...FORM_VACIO, nombre: inicial?.nombre ?? "", codigo_barras: inicial?.gtin ?? "" }));
  const [desdeMaestro, setDesdeMaestro] = useState<MaestroFila | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Alta escaneando el código (C-2, tramo 07): el código buscado en el catálogo nacional. Si el alta
  // llega con un código (del Mostrador o de una nota «Por crear»), se busca solo.
  const [codigoBuscado, setCodigoBuscado] = useState(inicial?.gtin ?? "");
  const porCodigo = useApi<{ producto: MaestroFila | null }>(
    codigoBuscado ? `/maestro/por-gtin/${encodeURIComponent(codigoBuscado)}` : null,
    [codigoBuscado],
  );
  const hallado = porCodigo.data?.producto ?? null;
  useEffect(() => {
    if (hallado && hallado.gtin === codigoBuscado) precargar(hallado);
  }, [hallado, codigoBuscado]);

  // Alta asistida (B7.4): elegir del maestro precarga el form; todo queda editable.
  function precargar(m: MaestroFila) {
    setF((f) => ({
      ...f,
      nombre: [m.nombre, m.concentracion].filter(Boolean).join(" "),
      presentacion: m.presentacion ? `${m.presentacion}${m.unidades_envase && m.unidades_envase > 1 ? ` x${m.unidades_envase}` : ""}` : f.presentacion,
      laboratorio: m.laboratorio ?? "",
      principio_activo: [m.dci, m.concentracion].filter(Boolean).join(" "),
      codigo_barras: m.gtin ?? f.codigo_barras,
    }));
    setDesdeMaestro(m);
  }

  function buscarCodigo() {
    const c = f.codigo_barras.trim();
    if (c) setCodigoBuscado(c);
  }

  async function crear() {
    if (!f.nombre.trim()) {
      setError("Nombre requerido");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await mutar<{ id: string }>("/catalogo/productos", { method: "POST", body: f });
      // La nota «Por crear» de la que salió este alta queda resuelta con la ficha nueva. Si esto falla,
      // el producto ya existe: la nota sigue en la lista y se puede descartar a mano.
      if (inicial?.notaId) {
        await mutar(`/productos-por-crear/${inicial.notaId}`, { method: "PATCH", body: { resolucion: "creado", producto_id: r.id } }).catch(() => undefined);
      }
      onCreado({ id: r.id, nombre: f.nombre.trim() });
      toast(`Producto "${f.nombre.trim()}" creado.`);
      setF(FORM_VACIO);
      setDesdeMaestro(null);
      setAbierto(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card className="gap-0">
      <div className="flex items-center justify-between">
        <h2 className="text-[13.5px] font-bold text-ink">Nuevo producto</h2>
        <Button variant="outline" size="sm" onClick={() => setAbierto((v) => !v)} className="max-md:min-h-[44px]">
          {abierto ? "Cerrar" : "+ Crear"}
        </Button>
      </div>
      {abierto && (
        <div className="mt-3 space-y-2">
          {/* Alta escaneando el código (C-2): el lector de barras escribe en el campo y termina con Enter. */}
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <Input
                value={f.codigo_barras}
                onChange={(e) => setF({ ...f, codigo_barras: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    buscarCodigo();
                  }
                }}
                aria-label="Código de barras"
                placeholder="Código de barras"
                className="font-mono"
              />
            </div>
            <Button variant="outline" size="sm" onClick={buscarCodigo} disabled={!f.codigo_barras.trim()} className="shrink-0 whitespace-nowrap max-md:min-h-[44px]">
              Buscar código
            </Button>
          </div>
          {codigoBuscado && porCodigo.cargando && <p className="text-[12px] text-ink-2">Buscando {codigoBuscado} en el catálogo nacional…</p>}
          {codigoBuscado && !porCodigo.cargando && porCodigo.data && !porCodigo.data.producto && (
            <p className="text-[12px] text-ink-2">El código {codigoBuscado} no está en el catálogo nacional: llena la ficha a mano.</p>
          )}
          {codigoBuscado && !porCodigo.cargando && porCodigo.error && <p className="text-[12px] text-accent-ink">{porCodigo.error}</p>}
          <BuscadorMaestro onElegir={precargar} />
          {desdeMaestro && (
            <p className="rounded-[9px] bg-info-soft px-3 py-1.5 text-[12px] text-info-ink">
              Precargado del catálogo nacional{desdeMaestro.gtin ? ` · GTIN ${desdeMaestro.gtin}` : ""} — revisa y ajusta lo que quieras.
            </p>
          )}
          <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Nombre *" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.presentacion} onChange={(e) => setF({ ...f, presentacion: e.target.value })} placeholder="Presentación (texto)" />
            <Input value={f.laboratorio} onChange={(e) => setF({ ...f, laboratorio: e.target.value })} placeholder="Laboratorio" />
            <Input value={f.principio_activo} onChange={(e) => setF({ ...f, principio_activo: e.target.value })} placeholder="Principio activo" />
            <Input value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} placeholder="Categoría" />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            <input type="checkbox" className="accent-accent" checked={f.requiere_receta} onChange={(e) => setF({ ...f, requiere_receta: e.target.checked })} /> Requiere receta
          </label>
          {error && <p className="text-[12px] text-accent-ink">{error}</p>}
          <Button onClick={() => void crear()} disabled={enviando} className="max-md:min-h-[44px]">
            {enviando ? "Creando..." : "Crear (con presentación base)"}
          </Button>
        </div>
      )}
    </Card>
  );
}

// Buscador contra el catálogo maestro nacional (15,181 productos DIGEMID/SUSALUD). Para aseo,
// galénicos y misceláneas (que NO están en el maestro) simplemente se llena el form a mano.
function BuscadorMaestro({ onElegir }: { onElegir: (m: MaestroFila) => void }) {
  const [q, setQ] = useState("");
  const [qLista, setQLista] = useState(""); // valor "asentado" tras el debounce
  const [mostrar, setMostrar] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setQLista(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const busqueda = useApi<{ resultados: MaestroFila[] }>(
    qLista.length >= 3 ? `/maestro/buscar?q=${encodeURIComponent(qLista)}` : null,
    [qLista],
  );
  const resultados = busqueda.data?.resultados ?? [];

  return (
    <div className="space-y-1 rounded-[9px] border border-line-inset bg-inset p-2">
      <Input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setMostrar(true);
        }}
        placeholder="🔎 Buscar en el catálogo nacional"
      />
      {qLista.length >= 3 && mostrar && (
        <div className="max-h-52 overflow-y-auto">
          {busqueda.cargando ? (
            <p className="px-2 py-1 text-[12px] text-ink-3">Buscando…</p>
          ) : resultados.length === 0 ? (
            <p className="px-2 py-1 text-[12px] text-ink-3">Sin resultados en el maestro (si es aseo/galénico, llena el form a mano).</p>
          ) : (
            <ul className="divide-y divide-line-row">
              {resultados.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => {
                      onElegir(m);
                      setMostrar(false);
                    }}
                    className="w-full rounded-[9px] px-2 py-1.5 text-left hover:bg-hover-btn"
                  >
                    <span className="text-[13px] font-semibold text-ink">{[m.nombre, m.concentracion].filter(Boolean).join(" ")}</span>
                    <span className="block text-[12px] text-ink-3">
                      {[m.laboratorio, m.presentacion, m.unidades_envase && m.unidades_envase > 1 ? `x${m.unidades_envase}` : null, m.gtin ? `GTIN ${m.gtin}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function EditorPrecios({ productoId, esSuper, sucursalId }: { productoId: string; esSuper: boolean; sucursalId: string }) {
  const q = esSuper && sucursalId ? `?sucursal_id=${sucursalId}` : "";
  const pres = useApi<{ presentaciones: Presentacion[] }>(`/catalogo/productos/${productoId}/presentaciones`, [productoId]);
  const precios = useApi<{ precios: Precio[] }>(`/precios?producto_id=${productoId}${esSuper && sucursalId ? `&sucursal_id=${sucursalId}` : ""}`, [productoId, sucursalId]);
  const [nuevaPres, setNuevaPres] = useState({ nombre: "", factor: "" });
  const recargar = () => {
    pres.recargar();
    precios.recargar();
  };

  const precioDe = (presId: string) => precios.data?.precios.find((p) => p.presentacion_id === presId) ?? null;

  async function agregarPresentacion() {
    const factor = Number(nuevaPres.factor);
    if (!nuevaPres.nombre.trim() || !Number.isInteger(factor) || factor < 1) return;
    await mutar(`/catalogo/productos/${productoId}/presentaciones`, { method: "POST", body: { nombre: nuevaPres.nombre.trim(), factor_unidades: factor } });
    setNuevaPres({ nombre: "", factor: "" });
    recargar();
  }

  if (pres.cargando) return <p className="py-4 text-center text-[13px] text-ink-3">Cargando presentaciones…</p>;

  return (
    <div className="space-y-3">
      {(pres.data?.presentaciones.length ?? 0) === 0 ? (
        <p className="text-[13px] text-ink-3">Sin presentaciones.</p>
      ) : (
        <ul className="space-y-2">
          {pres.data!.presentaciones.map((p) => (
            <FilaPrecio key={p.id} pres={p} precio={precioDe(p.id)} productoId={productoId} q={q} onGuardado={recargar} />
          ))}
        </ul>
      )}
      {/* El ancho lo ponen los contenedores: `Input` trae `w-full` y un `w-20` en el propio campo no
          le gana (trampa del tramo 03). En el celular el botón baja de renglón. */}
      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <div className="min-w-[160px] flex-1">
          <Input value={nuevaPres.nombre} onChange={(e) => setNuevaPres({ ...nuevaPres, nombre: e.target.value })} placeholder="Presentación (blíster x10)" />
        </div>
        <div className="w-20">
          <Input value={nuevaPres.factor} onChange={(e) => setNuevaPres({ ...nuevaPres, factor: e.target.value })} type="number" min={1} placeholder="factor" className="tabular-nums" />
        </div>
        <Button variant="outline" size="sm" onClick={() => void agregarPresentacion()} className="min-h-[44px] whitespace-nowrap">
          + Presentación
        </Button>
      </div>
    </div>
  );
}

function FilaPrecio({ pres, precio, productoId, q, onGuardado }: { pres: Presentacion; precio: Precio | null; productoId: string; q: string; onGuardado: () => void }) {
  const toast = useToast();
  const [venta, setVenta] = useState("");
  const [compra, setCompra] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    const dmVenta = aDm(venta);
    if (dmVenta === null) {
      setError("Precio de venta inválido (ej. 12.50)");
      return;
    }
    const dmCompra = compra.trim() ? aDm(compra) : null;
    if (compra.trim() && dmCompra === null) {
      setError("Precio de compra inválido");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await mutar(`/precios${q}`, {
        method: "POST",
        body: { producto_id: productoId, presentacion_id: pres.id, precio_sin_igv_dm: dmVenta, precio_compra_dm: dmCompra ?? undefined },
      });
      setVenta("");
      setCompra("");
      toast(`Precio de "${pres.nombre}" fijado.`);
      onGuardado();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <li className="rounded-[9px] border border-line bg-inset px-3 py-2.5">
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-ink">
          {pres.nombre} <span className="text-ink-3">(×{pres.factor_unidades})</span>
        </span>
        <span className="font-mono tabular-nums text-ink-2">{precio ? solesDm(precio.precio_total_dm) : "sin precio"}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input value={venta} onChange={(e) => setVenta(e.target.value)} inputMode="decimal" placeholder="Venta s/IGV" className="flex-1 font-mono tabular-nums" />
        <Input value={compra} onChange={(e) => setCompra(e.target.value)} inputMode="decimal" placeholder="Compra" className="flex-1 font-mono tabular-nums" />
        <Button onClick={() => void guardar()} disabled={guardando} size="sm" className="max-md:min-h-[44px]">
          {guardando ? "..." : "Fijar"}
        </Button>
      </div>
      {error && <p className="mt-1 text-[12px] text-accent-ink">{error}</p>}
    </li>
  );
}
